import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "@/components/Logo";

const links = [
  { to: "/", label: "Home" },
  { to: "/the-social-bar", label: "The Social Bar" },
  { to: "/the-social-set", label: "The Social Set" },
  { to: "/nightclub-rentals", label: "Nightclub Rentals" },
  { to: "/the-social-supply", label: "The Social Supply" },
  { to: "/availability", label: "Availability" },
  { to: "/events", label: "Events" },
  { to: "/gift-cards", label: "Gift Cards" },
  { to: "/about", label: "About" },
];

// Pages that open with a dark full-bleed hero — nav starts in on-dark palette.
const DARK_HERO_PATHS = ["/", "/the-social-bar", "/the-social-set", "/nightclub-rentals"];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();
  const hasDarkHero = DARK_HERO_PATHS.includes(loc.pathname);
  const useDarkPalette = hasDarkHero && !scrolled; // ivory text over hero photo, else dark text on ivory

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); window.scrollTo(0, 0); }, [loc.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${useDarkPalette ? "on-dark" : ""}`}
      style={{
        backgroundColor: scrolled
          ? "rgba(245,241,232,0.88)"
          : (hasDarkHero ? "rgba(15,14,12,0.18)" : "rgba(245,241,232,0.55)"),
        backdropFilter: "blur(18px)",
        WebkitBackdropFilter: "blur(18px)",
        borderBottom: scrolled ? "1px solid var(--ss-border)" : "1px solid transparent",
      }}
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-4 md:py-5 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="leading-none shrink-0">
          <Logo size="sm" />
        </Link>

        <nav className="hidden xl:flex items-center gap-6">
          {links.slice(1).map(l => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to.replace(/\//g,'') || 'home'}`}
              className={({ isActive }) =>
                `font-label text-[0.7rem] whitespace-nowrap transition-colors duration-300 ${isActive ? "text-[color:var(--ss-rust)]" : "hover:text-[color:var(--ss-rust)]"}`
              }
              style={{ color: undefined }}
            >
              {l.label}
            </NavLink>
          ))}
          <Link to="/book" data-testid="nav-book-btn" className="btn-primary">
            Book <ArrowUpRight className="arrow" size={14} />
          </Link>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(o => !o)}
          className="xl:hidden p-2"
          style={{ color: "var(--ss-cream)" }}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-panel"
          className="xl:hidden border-t on-dark"
          style={{ borderColor: "var(--ss-border)", background: "rgba(15,14,12,0.98)" }}
        >
          <div className="px-6 py-8 flex flex-col gap-5">
            {links.map(l => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-link-${l.to.replace(/\//g,'') || 'home'}`}
                className={({ isActive }) =>
                  `font-display text-3xl tracking-tight ${isActive ? "text-[color:var(--ss-rust)]" : ""}`
                }
                style={{ color: undefined }}
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/book" data-testid="mobile-nav-book-btn" className="btn-primary mt-4 w-fit">
              Book Your Event <ArrowUpRight className="arrow" size={14} />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
